import { SHOW_PREVIEW_BADGE } from "@/lib/environment";
import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  Home, Search, MessagesSquare, Users, UserCheck, CalendarDays, UsersRound, MessageSquare, CalendarCheck,
  Bookmark, Bell, IdCard, User, ShieldCheck, Settings, HelpCircle, LogOut, WifiOff, LockKeyhole, Info,
} from "lucide-react";

import { useI18n } from "@/i18n/I18nContext";
import { useMemberAuth } from "../MemberAuthContext";
import { useOnline } from "../useCore";
import { SIDE_NAV, BOTTOM_NAV, navSection, bottomSection, crumbLabel, shortName } from "../navSections";
import { Avatar } from "./kit";

// Full area list (used by the "Profil / Mehr" overview); the V3 sidebar itself shows SIDE_NAV.
export const MEMBER_NAV = [
  { key: "start", path: "/", icon: Home, label: "Start", end: true },
  { key: "search", path: "/suche", icon: Search, label: "Suche" },
  { key: "community", path: "/community", icon: MessagesSquare, label: "Community" },
  { key: "people", path: "/menschen", icon: Users, label: "Menschen" },
  { key: "contacts", path: "/kontakte", icon: UserCheck, label: "Kontakte" },
  { key: "activities", path: "/aktivitaeten", icon: CalendarDays, label: "Aktivitäten" },
  { key: "groups", path: "/kreise", icon: UsersRound, label: "Kreise & Gruppen" },
  { key: "messages", path: "/nachrichten", icon: MessageSquare, label: "Nachrichten" },
  { key: "plans", path: "/plaene", icon: CalendarCheck, label: "Meine Pläne" },
  { key: "saved", path: "/merkliste", icon: Bookmark, label: "Merkliste" },
  { key: "notifications", path: "/benachrichtigungen", icon: Bell, label: "Benachrichtigungen" },
];
export const PERSONAL_NAV = [
  { key: "pass", path: "/access-pass", icon: IdCard, label: "Mein Access Pass" },
  { key: "profile", path: "/profil", icon: User, label: "Mein Profil" },
  { key: "safety", path: "/sicherheit", icon: ShieldCheck, label: "Sicherheit" },
  { key: "settings", path: "/einstellungen", icon: Settings, label: "Einstellungen" },
  { key: "help", path: "/hilfe", icon: HelpCircle, label: "Hilfe" },
];

const ICONS = { start: Home, discover: Search, activities: CalendarDays, groups: UsersRound, messages: MessageSquare, plans: CalendarCheck, profile: User, pass: LockKeyhole, settings: Settings, help: Info };

// Original VISIBILIS.LIFE mark with the wordmark set in Noto Sans (V3 lockup).
export function BrandLockup({ dark = false }) {
  return (
    <span className="flex items-center gap-[10px]">
      <span className={`flex h-[31px] w-[37px] items-center justify-center rounded-[6px] ${dark ? "bg-white" : ""}`}>
        <img src="/visibilis-mark.png" alt="" className="h-[27px] w-auto" />
      </span>
      <span className="flex flex-col leading-none">
        <span className={`font-bold tracking-[0.01em] ${dark ? "text-[15px] text-white" : "text-[16px] text-v3-ink"}`}>VISIBILIS.LIFE</span>
        <span className={`mt-[4px] text-[6px] font-bold uppercase tracking-[0.02em] ${dark ? "text-v3-pink" : "text-v3-magenta"}`}>Menschen mit Behinderungen</span>
      </span>
    </span>
  );
}

// Navy V3 sidebar (238 px); the access pages use it without navigation.
export function V3Sidebar({ children }) {
  const { t } = useI18n();
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-[238px] flex-col bg-v3-navy lg:flex" aria-label="Hauptnavigation">
      <Link to="/" aria-label="VISIBILIS.LIFE – zur Startseite" className="px-[18px] pt-[25px]"><BrandLockup dark /></Link>
      <p className="px-[22px] pt-[30px] text-[13px] font-bold text-v3-side">Mitglieder-App</p>
      {children}
      <div className="mt-auto px-[22px] pb-[28px] text-[10px] leading-[20px] text-v3-sidefoot" data-testid="member-env-footer">
        <p>Hilfe bleibt erreichbar</p>
        <p>VISIBILIS.LIFE</p>
        {SHOW_PREVIEW_BADGE && <p className="mt-1 font-bold text-v3-pink" data-testid="member-env-badge">{t("app.env_preview")}</p>}
      </div>
    </aside>
  );
}

function SideLink({ item, active }) {
  const Icon = ICONS[item.key];
  return (
    <Link to={item.path} data-testid={`member-nav-${item.key}`} aria-current={active ? "page" : undefined}
      className={`flex min-h-[41px] items-center gap-[14px] rounded-[8px] px-[12px] text-[13px] text-white transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${active ? "bg-v3-magenta font-bold" : "font-normal hover:bg-white/10"}`}>
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" /><span className="truncate">{item.label}</span>
    </Link>
  );
}

function BottomLink({ item, active }) {
  const Icon = ICONS[item.key];
  return (
    <Link to={item.path} data-testid={`member-bottomnav-${item.key}`} aria-current={active ? "page" : undefined}
      className={`relative flex min-h-[64px] flex-1 flex-col items-center justify-center gap-[6px] text-[11.5px] ${active ? "font-bold text-v3-magenta" : "font-normal text-v3-ink"}`}>
      <Icon className="h-5 w-5" aria-hidden="true" /><span>{item.label}</span>
      {active && <span aria-hidden="true" className="absolute bottom-[6px] h-[3px] w-[32px] rounded-full bg-v3-magenta" />}
    </Link>
  );
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" data-testid="member-offline-banner" className="flex items-center gap-2 bg-v3-sand px-4 py-2 text-sm font-medium text-v3-amber">
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      Keine Internetverbindung. Angezeigte Inhalte können veraltet sein; Senden ist erst wieder möglich, wenn die Verbindung zurück ist.
    </div>
  );
}

function UserMenu({ member, onLogout, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  const label = shortName(member?.name);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true" data-testid={compact ? "member-mobile-usermenu" : "member-topbar-profile"}
        aria-label={compact ? `Konto-Menü${label ? ` für ${label}` : ""}` : undefined}
        className="flex min-h-[44px] items-center gap-[12px] rounded-full pr-1 text-[12.5px] font-bold text-v3-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-v3-magenta">
        <Avatar name={member?.name} color={member?.avatar_color} size={compact ? "h-[38px] w-[38px] text-xs" : "h-[40px] w-[40px] text-xs"} />
        {!compact && <span className="max-w-[28vw] truncate" title={member?.name} data-testid="member-topbar-name">{label ? `${label} · Mitglied` : "Mitglied"}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-56 rounded-[12px] border border-v3-line bg-white p-2 shadow-lg" data-testid="member-usermenu">
          <Link to="/profil" className="flex min-h-[44px] items-center gap-2 rounded-[8px] px-3 text-[14px] font-bold text-v3-ink hover:bg-v3-lilac" data-testid="member-usermenu-profile"><User className="h-4 w-4" aria-hidden="true" />Mein Profil</Link>
          <button type="button" onClick={onLogout} data-testid="member-logout" className="flex min-h-[44px] w-full items-center gap-2 rounded-[8px] px-3 text-left text-[14px] font-bold text-v3-magenta hover:bg-v3-blush">
            <LogOut className="h-4 w-4" aria-hidden="true" />Abmelden
          </button>
        </div>
      )}
    </div>
  );
}

function Pill({ to, children, testid }) {
  return <Link to={to} data-testid={testid} className="inline-flex min-h-[29px] items-center rounded-full bg-v3-lilac px-[12px] text-[12.5px] font-bold text-v3-ink hover:bg-v3-line">{children}</Link>;
}

export function MemberLayout({ children }) {
  const { t } = useI18n();
  const { member, logout } = useMemberAuth();
  const { pathname } = useLocation();
  const section = navSection(pathname);
  const bottom = bottomSection(pathname);
  const name = shortName(member?.name);

  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/member-sw.js", { scope: "/" }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-v3-canvas text-v3-ink">
      <a href="#member-main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-v3-magenta focus:px-4 focus:py-2 focus:text-white">
        {t("common.skip_to_content")}
      </a>

      <V3Sidebar>
        <nav className="mt-[19px] space-y-[4px] px-[12px]" aria-label="Bereiche">{SIDE_NAV.map((i) => <SideLink key={i.key} item={i} active={section === i.key} />)}</nav>
      </V3Sidebar>

      <div className="lg:pl-[238px]">
        <header className="sticky top-0 z-20 border-b border-v3-line bg-white">
          <div className="hidden h-[82px] items-center px-[33px] lg:flex min-[1400px]:pr-[217px]">
            <p className="flex-1 text-[17px] font-bold text-v3-ink">Mitglieder-App</p>
            <Pill to="/einstellungen/region" testid="member-topbar-region">Region</Pill>
            <span className="ml-[62px]"><Pill to="/einstellungen/region" testid="member-topbar-language">Deutsch</Pill></span>
            <span className="ml-[45px]"><UserMenu member={member} onLogout={logout} /></span>
          </div>
          <div className="flex h-[76px] items-center gap-3 px-[17px] lg:hidden">
            <Link to="/" aria-label="VISIBILIS.LIFE – zur Startseite" className="flex-1"><BrandLockup /></Link>
            {SHOW_PREVIEW_BADGE && <span className="rounded-full bg-v3-lilac px-2 py-1 text-[11px] font-bold text-v3-muted" data-testid="member-env-badge-mobile">{t("app.env_preview")}</span>}
            <UserMenu member={member} onLogout={logout} compact />
          </div>
          <OfflineBanner />
        </header>

        <main id="member-main" tabIndex={-1} className="mx-auto max-w-[1210px] px-5 pb-28 pt-[14px] outline-none lg:pb-12 lg:pl-[38px] lg:pr-[40px] lg:pt-[25px]">
          <p className="hidden text-[12px] font-bold leading-[18px] text-v3-muted lg:block" data-testid="member-breadcrumb">{crumbLabel(pathname)}</p>
          <p className="text-[12.5px] font-bold text-v3-muted lg:hidden" data-testid="member-mobile-identity">{name ? `${name} · Mitglied` : "Mitglied"}</p>
          <div className="mt-[14px] lg:mt-[18px]">{children}</div>
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex h-[68px] border-t border-v3-line bg-white lg:hidden" aria-label="Navigation unten">
        {BOTTOM_NAV.map((i) => <BottomLink key={i.key} item={i} active={bottom === i.key} />)}
      </nav>
    </div>
  );
}
